import * as Api from '@ViewModels';
import type { BaseNodeData } from '../types';
import { NODE_TYPES } from '../types';

export type TriggerAutomationStepLike = Omit<Api.IAutomationStep, '_type'> & {
	_type?: Api.ApiModelType | 'TriggerAutomationStep';
	targetAutomationTemplateId?: string;
	targetAutomationTemplateName?: string;
};

export type AutomationTemplateTargetSummary = Api.IAutomationTemplate & {
	archived?: boolean;
	isArchived?: boolean;
	publishedSteps?: Api.IAutomationStepReference[];
	draftSteps?: Api.IAutomationStepReference[];
	runtimeDays?: number;
};

// User-facing messages. Toast variants end with a period; validation-result variants omit it
// to compose with `${stepLabel}: ${MESSAGE}`. Keep both shapes to avoid breaking existing tests
// that assert on substrings (e.g. 'must be the last step').
export const TRIGGER_AUTOMATION_LAST_STEP_MESSAGE = 'Start Automation must be the last step';
export const TRIGGER_AUTOMATION_LAST_STEP_TOAST = 'Start Automation must be the last step.';
export const TRIGGER_AUTOMATION_TRIGGER_TYPE_TOAST =
	'Start Automation is only available for manual or tag-triggered automations.';

const MANUAL_OR_TAG_TRIGGER_TYPES: ReadonlySet<string> = new Set([
	Api.AutomationTriggerType.Tag,
	'TagAutomationTrigger',
	Api.AutomationTriggerType.Manual,
	'ManualAutomationTrigger',
]);

/**
 * TriggerAutomationStep is only valid on manual automations (no trigger object)
 * or tag-triggered automations. This mirrors the API publisher rule.
 */
export function canUseTriggerAutomationStep(rootTrigger: Api.IAutomationTrigger | null | undefined): boolean {
	if (!rootTrigger) {
		return true;
	}

	const triggerType = (rootTrigger as { _type?: string })._type;
	return !!triggerType && MANUAL_OR_TAG_TRIGGER_TYPES.has(triggerType);
}

export function isTriggerAutomationStepNode(node: { type?: string; data?: BaseNodeData } | null | undefined): boolean {
	return node?.type === NODE_TYPES.TRIGGER_AUTOMATION_STEP;
}

export function isTriggerAutomationStep(step: unknown): step is TriggerAutomationStepLike {
	return (step as { _type?: unknown } | undefined)?._type === 'TriggerAutomationStep';
}

export function getPublishedTrigger(template: Api.IAutomationTemplate): Api.IAutomationTrigger | undefined | null {
	const templateLike = template as {
		published?: { trigger?: Api.IAutomationTrigger | null };
		publishedTrigger?: Api.IAutomationTrigger | null;
	};

	if ('publishedTrigger' in templateLike) {
		return templateLike.publishedTrigger;
	}

	return templateLike.published?.trigger;
}

function isArchivedTemplate(template: AutomationTemplateTargetSummary): boolean {
	return template.status === Api.TemplateStatus.Archived || template.archived === true || template.isArchived === true;
}

export function isEligibleTriggerAutomationTarget(
	template: Api.IAutomationTemplate | null | undefined,
	currentTemplateId?: string
): boolean {
	if (!template?.id) {
		return false;
	}
	if (currentTemplateId && template.id === currentTemplateId) {
		return false;
	}
	if (isArchivedTemplate(template as AutomationTemplateTargetSummary)) {
		return false;
	}

	return canUseTriggerAutomationStep(getPublishedTrigger(template));
}

export function shouldUseAnchoredSchedule(trigger: Api.IAutomationTrigger | null | undefined): boolean {
	return (
		trigger?._type === Api.AutomationTriggerType.Meeting ||
		trigger?._type === Api.AutomationTriggerType.EventRegistration ||
		Api.VmUtils.Automations.triggers.triggerUsesRelativeToAnchorScheduleCriteria(trigger || undefined)
	);
}

export function normalizeTriggerAutomationStepSchedule(
	schedule: Api.IAutomationStepSchedule | null | undefined,
	rootTrigger?: Api.IAutomationTrigger | null,
	options: { normalizeToRootTrigger?: boolean } = {}
): Api.IAutomationStepSchedule | null | undefined {
	if (!schedule) {
		return schedule;
	}

	if (
		schedule.criteria === Api.AutomationStepScheduleCriteria.Conditional ||
		schedule.criteria === Api.AutomationStepScheduleCriteria.Scheduled
	) {
		return schedule;
	}

	const shouldAnchor = shouldUseAnchoredSchedule(rootTrigger);
	if (schedule.criteria === Api.AutomationStepScheduleCriteria.Immediately) {
		return {
			...schedule,
			criteria: shouldAnchor
				? Api.AutomationStepScheduleCriteria.RelativeToAnchor
				: Api.AutomationStepScheduleCriteria.AfterAutomationStart,
			numberOfDays: 0,
			numberOfHours: 0,
			numberOfMinutes: 0,
		};
	}

	if (!options.normalizeToRootTrigger) {
		return schedule;
	}

	// RelativeToAnchor schedules use signed offsets (negative = before anchor, positive = after).
	// Math.abs would silently flip "before" to "after"; preserve the sign when anchoring.
	const normalizeNumber = shouldAnchor
		? (value: number | null | undefined) => value ?? 0
		: (value: number | null | undefined) => Math.abs(value ?? 0);
	return {
		...schedule,
		criteria: shouldAnchor
			? Api.AutomationStepScheduleCriteria.RelativeToAnchor
			: Api.AutomationStepScheduleCriteria.AfterAutomationStart,
		numberOfDays: normalizeNumber(schedule.numberOfDays),
		numberOfHours: normalizeNumber(schedule.numberOfHours),
		numberOfMinutes: normalizeNumber(schedule.numberOfMinutes),
	};
}

export function getTriggerAutomationTargetEligibilityError(
	step: TriggerAutomationStepLike,
	targetTemplates: Api.IAutomationTemplate[] | undefined,
	currentTemplateId?: string
): string | null {
	if (!step.targetAutomationTemplateId || !targetTemplates) {
		return null;
	}

	// The published-templates endpoint can return null entries for templates whose
	// Creator user cannot be resolved (see AutomationTemplateResolver.cs implicit
	// OperationResult<T> → T conversion). Guard the predicate accordingly.
	const targetTemplate = targetTemplates.find(template => template?.id === step.targetAutomationTemplateId);
	if (!targetTemplate) {
		return 'Target automation is no longer eligible. Select another automation to start.';
	}

	if (!isEligibleTriggerAutomationTarget(targetTemplate, currentTemplateId)) {
		return 'Target automation is no longer eligible. Select a manual or tag-triggered automation.';
	}

	return null;
}
